import { useQuery } from '@tanstack/react-query';

import type { TrialBalanceResponse, TrialBalanceRow } from '@dcc-erp/shared';

import { api } from '../api';

// 'account' (default, one row per accountCode) | 'account-branch' |
// 'account-department' (one row per accountCode + dimension). Mirrors the API.
export type TrialBalanceGroupBy = 'account' | 'account-branch' | 'account-department';

export interface TrialBalanceFilters {
  // 'YYYYMM'
  period?: string;
  // ISO YYYY-MM-DD
  dateFrom?: string;
  dateTo?: string;
  // Line-level dimension filters (Phase 42-A4). UUIDs.
  branchId?: string;
  departmentId?: string;
  // Aggregation grouping; defaults to 'account' when omitted.
  groupBy?: TrialBalanceGroupBy;
}

// When grouped by a dimension the API adds dimensionId + dimensionName to each
// row. The shared TrialBalanceRowSchema (owned by another agent) doesn't model
// these, so widen the wire shape here.
export type TrialBalanceRowWithDim = TrialBalanceRow & {
  dimensionId?: string | null;
  dimensionName?: string;
};
export type TrialBalanceResponseWithDim = Omit<TrialBalanceResponse, 'rows'> & {
  rows: TrialBalanceRowWithDim[];
};

export function useTrialBalance(companyId: string | undefined, filters: TrialBalanceFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.groupBy) qs.set('groupBy', filters.groupBy);
  const search = qs.toString();
  return useQuery({
    queryKey: ['trial-balance', companyId, filters],
    queryFn: () =>
      api<TrialBalanceResponseWithDim>(
        `/companies/${companyId}/trial-balance${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}
